import { computed, onMounted, onBeforeUnmount, type Ref } from 'vue'
import { useStore } from '../store/index'
import type { EstiloWeb } from '../types/web'

/**
 * Plantilla 2 de las webs de notaría (ref: https://trello.com/c/3g3PTnq5).
 * Lee los mismos datos que la plantilla de siempre (NOTARIA): lo que se cambia en las secciones
 * del panel se ve aquí igual. Lo único propio es el estilo y el color de acento (NOTARIA.Web).
 */

const ESTILOS: EstiloWeb[] = ['moderno', 'clasico', 'elegante', 'elegante-claro']

/** Fuentes de cada estilo (Google Fonts), cargadas solo en las webs con esta plantilla. */
export const FUENTES_ESTILO: Record<EstiloWeb, string> = {
  moderno: 'https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,500;12..96,700&family=Figtree:wght@400;500;600&display=swap',
  clasico: 'https://fonts.googleapis.com/css2?family=Newsreader:opsz,wght@6..72,500;6..72,600&family=Public+Sans:wght@400;500;600&display=swap',
  elegante: 'https://fonts.googleapis.com/css2?family=Instrument+Serif&family=Manrope:wght@400;500;600&display=swap',
  // Elegante en claro: las mismas fuentes que el oscuro (ref: https://trello.com/c/6B7Rn53E)
  'elegante-claro': 'https://fonts.googleapis.com/css2?family=Instrument+Serif&family=Manrope:wght@400;500;600&display=swap',
}

/** Acento de cada estilo cuando la notaría no tiene ni acento ni color de menú válido. */
const ACENTO_ESTILO: Record<EstiloWeb, string> = { moderno: '#2f5bff', clasico: '#8c2f39', elegante: '#d7b46a', 'elegante-claro': '#9a7b3c' }

/** '#RGB', '#RRGGBB' o '#RRGGBBAA' → '#rrggbb' (sin transparencia), o null si no es un color usable. */
export function colorHex(valor?: string | null): string | null {
  const v = (valor || '').trim()
  let m = /^#([0-9a-f]{3})$/i.exec(v)
  if (m) return '#' + m[1].split('').map(c => c + c).join('').toLowerCase()
  m = /^#([0-9a-f]{6})([0-9a-f]{2})?$/i.exec(v)
  if (!m) return null
  // Un color casi transparente no sirve de acento
  if (m[2] && parseInt(m[2], 16) < 0x80) return null
  return '#' + m[1].toLowerCase()
}

function luminancia(hex: string): number {
  const [r, g, b] = [1, 3, 5].map(i => {
    const c = parseInt(hex.slice(i, i + 2), 16) / 255
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
  })
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** Texto legible sobre el acento (botones): blanco o casi negro, el de más contraste. */
export function textoSobre(hex: string): string {
  const l = luminancia(hex)
  return (1.05 / (l + 0.05)) >= ((l + 0.05) / 0.0625) ? '#ffffff' : '#14161a'
}

export function useNotaria2() {
  const store = useStore()

  const estilo = computed<EstiloWeb>(() => {
    const e = String(store.NOTARIA?.Web?.Estilo || '').toLowerCase() as EstiloWeb
    return ESTILOS.includes(e) ? e : 'moderno'
  })

  /** Acento elegido; si no hay, el color del menú de la notaría (lo que ya configuró en el panel). */
  const acento = computed(() =>
    colorHex(store.NOTARIA?.Web?.Acento)
    ?? colorHex(store.NOTARIA?.Theme?.Menu?.Fondo?.BackgroundColor)
    ?? ACENTO_ESTILO[estilo.value])

  // El logo de la notaría está pensado para el fondo de su menú (a menudo es blanco sobre un color):
  // se pinta siempre sobre ese fondo para que se vea con cualquier estilo
  const fondoLogo = computed(() => colorHex(store.NOTARIA?.Theme?.Menu?.Fondo?.BackgroundColor) ?? 'transparent')

  const variables = computed(() => ({
    '--n2-acento': acento.value,
    '--n2-sobre-acento': textoSobre(acento.value),
    '--n2-fondo-logo': fondoLogo.value,
  }))

  return { estilo, acento, variables }
}

/** Título de cada página del menú: el que puso la notaría en el panel o el texto por defecto. */
export function tituloSeccion(notaria: any, seccion: string, $t: (k: string) => string): string {
  const T = notaria?.Theme ?? {}
  switch (seccion) {
    case 'Inicio': return T.Home?.Titulo?.Texto || $t('Inicio')
    case 'Notario': return T.Notarios?.Titulo?.Texto || $t((notaria?.Notarios?.length ?? 0) > 1 ? 'Notarios' : 'Notario')
    case 'Equipo': return T.Team?.Titulo?.Texto || $t('Equipo')
    case 'Servicios': return T.Servicios?.Titulo?.Texto || $t('Servicios')
    case 'Blog': return T.Blog?.Titulo?.Texto || $t('Blog')
    case 'Gallery': return T.Gallery?.Titulo?.Texto || $t('Galeria')
    case 'Contacto': return T.Contacto?.Titulo?.Texto || $t('Contacto')
  }
  return seccion
}

/** Nombre del enlace a la Notaría Virtual (Menú → "Nombre web" en el panel). */
export function tituloNV(notaria: any, $t: (k: string) => string): string {
  const nombre = String(notaria?.Theme?.Menu?.NameWeb || '').trim()
  if (!nombre || /^notar[ií]a\s?virtual$/i.test(nombre)) return $t('NotariaVirtual')
  return nombre
}

export function urlExterna(url?: string | null): string {
  if (!url) return ''
  return /^https?:\/\//i.test(url) ? url : 'http://' + url
}

/** Fecha del panel ("23/07/2024, 12:18:44" o ISO) en milisegundos, para ordenar. */
export function fechaNotin(valor?: string | null): number {
  if (!valor) return 0
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(valor)
  if (m) return new Date(+m[3], +m[2] - 1, +m[1]).getTime()
  const t = Date.parse(valor)
  return isNaN(t) ? 0 : t
}

const DIAS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/**
 * ¿Está abierta ahora? Con el horario estructurado (Contacto → Horario por tramos) y la hora de España.
 * null si la notaría no lo ha rellenado: entonces no se muestra nada.
 */
export function abiertoAhora(tramos: any, ahora = new Date()): boolean | null {
  const lista = (Array.isArray(tramos) ? tramos : []).filter((t: any) => t?.Dias?.length && t.Abre && t.Cierra)
  if (!lista.length) return null
  const partes = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Madrid', weekday: 'long', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(ahora)
  const dia = partes.find(p => p.type === 'weekday')?.value ?? DIAS[ahora.getDay()]
  const hora = `${partes.find(p => p.type === 'hour')?.value ?? '00'}:${partes.find(p => p.type === 'minute')?.value ?? '00'}`.replace(/^24/, '00')
  return lista.some((t: any) => t.Dias.includes(dia) && t.Abre <= hora && hora < t.Cierra)
}

const ENTIDADES: Record<string, string> = {
  nbsp: ' ', amp: '&', quot: '"', apos: "'", lt: '<', gt: '>', laquo: '«', raquo: '»', hellip: '…', ndash: '–', mdash: '—',
  aacute: 'á', eacute: 'é', iacute: 'í', oacute: 'ó', uacute: 'ú', Aacute: 'Á', Eacute: 'É', Iacute: 'Í', Oacute: 'Ó', Uacute: 'Ú',
  ntilde: 'ñ', Ntilde: 'Ñ', uuml: 'ü', Uuml: 'Ü', ccedil: 'ç', Ccedil: 'Ç', agrave: 'à', egrave: 'è', ograve: 'ò', iexcl: '¡', iquest: '¿', ordf: 'ª', ordm: 'º',
}

/** Texto plano de un HTML del panel (para extractos), con un máximo de caracteres cortando por palabra. */
export function textoPlano(html?: string | null, max = 0): string {
  const texto = String(html || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(+n))
    .replace(/&([a-zA-Z]+);/g, (m, e) => ENTIDADES[e] ?? m)
    .replace(/\s+/g, ' ')
    .trim()
  if (!max || texto.length <= max) return texto
  return texto.slice(0, max - 1).replace(/\s\S*$/, '') + '…'
}

export interface EnlaceMenu { clave: string; texto: string; to?: string; href?: string; externo?: boolean }

/**
 * Menú de las plantillas 2 y 3: mismas páginas y mismos interruptores que el menú de siempre
 * (panel: Menú → Mostrar …) y el cambio de idioma de la cabecera de siempre.
 */
export function useMenuNotaria() {
  const store = useStore()
  const route = useRoute()
  const { $t, getLocalePath } = useUtils()

  const enlaces = computed<EnlaceMenu[]>(() => {
    const n: any = store.NOTARIA
    const M = n?.Theme?.Menu ?? {}
    const lista: EnlaceMenu[] = []
    const add = (clave: string, mostrar: any, ruta: string) => {
      if (mostrar) lista.push({ clave, texto: tituloSeccion(n, clave, $t), to: getLocalePath(ruta) })
    }
    add('Inicio', M.ShowInicio, '/')
    add('Notario', M.ShowNotarios && n?.Notarios?.length, '/notarios')
    add('Equipo', M.ShowEquipo, '/equipo')
    add('Servicios', M.ShowServicios, '/servicios')
    add('Blog', M.ShowBlog, '/blog')
    add('Gallery', M.ShowGallery, '/gallery')
    if (M.ShowNV && n?.NotariaVirtual) {
      lista.push(n.NotariaVirtualFrame
        ? { clave: 'NV', texto: tituloNV(n, $t), to: getLocalePath('/NotariaVirtual') }
        : { clave: 'NV', texto: tituloNV(n, $t), href: urlExterna(n.NotariaVirtual), externo: true })
    }
    add('Contacto', M.ShowContacto, '/contacto')
    return lista
  })

  const idiomas = computed<any[]>(() => (store as any).languages ?? [])

  // El idioma va como primer segmento de la ruta, salvo el idioma principal
  function cambiarIdioma(locale: any) {
    const partes = route.fullPath.split('/')
    const principal = idiomas.value[0]?.Language ?? 'es'
    const tieneIdioma = partes[1]?.length === 2 && idiomas.value.some(l => l.Language === partes[1])
    if (locale.Language === principal) {
      if (tieneIdioma) partes.splice(1, 1)
    } else if (tieneIdioma) {
      partes[1] = locale.Language
    } else {
      partes.splice(1, 0, locale.Language)
    }
    ;(store as any).setIdioma(locale.Flag)
    location.href = location.origin + (partes.join('/') || '/')
  }

  return { enlaces, idiomas, cambiarIdioma }
}

/**
 * Aparición suave de los elementos .n2-aparece al hacer scroll. Sin JavaScript, o con "reducir movimiento",
 * todo se ve desde el principio; lo que ya está en pantalla no se anima.
 */
export function useAparicion(raiz: Ref<HTMLElement | null>) {
  let observador: IntersectionObserver | null = null
  onMounted(() => {
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    observador = new IntersectionObserver((entradas) => {
      entradas.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('n2-visible'); observador?.unobserve(e.target) }
      })
    }, { rootMargin: '0px 0px -8% 0px' })
    raiz.value?.querySelectorAll('.n2-aparece').forEach((el) => {
      if (el.getBoundingClientRect().top < window.innerHeight) return
      el.classList.add('n2-oculto')
      observador?.observe(el)
    })
  })
  onBeforeUnmount(() => observador?.disconnect())
}

const PAGINA_ACCION: Record<string, string> = { about: '/notarios', service: '/servicios', blog: '/blog', homegallery: '/gallery', contact: '/contacto', Inicio: '/', equipo: '/equipo' }
const SECCION_ACCION: Record<string, string> = { about: 'about', service: 'service', blog: 'blog', testimonial: 'testimonial', homegallery: 'homegallery', contact: 'contact' }

/** Acciones de los botones del banner (las mismas opciones que en el panel). `desplazar`: ir a la sección de la portada si existe. */
export function useAccionBanner() {
  const store = useStore()
  const { getLocalePath } = useUtils()
  return function ir(accion: string, desplazar: boolean) {
    if (!accion) return
    if (accion === 'chatbot') { (store as any).isChatbotOpen = true; return }
    if (accion === 'NV') { window.open(urlExterna(store.NOTARIA?.NotariaVirtual), '_blank'); return }
    const destino = desplazar && document.getElementById(SECCION_ACCION[accion] || '')
    if (destino) { window.scrollTo({ top: destino.getBoundingClientRect().top + window.scrollY - 80, behavior: 'smooth' }); return }
    if (PAGINA_ACCION[accion]) navigateTo(getLocalePath(PAGINA_ACCION[accion]))
    else if (SECCION_ACCION[accion]) document.getElementById(SECCION_ACCION[accion])?.scrollIntoView({ behavior: 'smooth' })
  }
}

/**
 * Páginas interiores con el diseño de las plantillas 2 a 5 (nombres de ruta sin el prefijo "lang-", en minúsculas).
 * Las demás (encargo online, Notaría Virtual…) siguen con sus componentes de siempre dentro de la cabecera y el pie nuevos.
 */
const RUTAS_DISENO_PROPIO = [
  'notarios', 'equipo', 'servicios', 'servicios-id', 'blog', 'blog-id', 'gallery', 'contacto',
  'avisolegal', 'politicacookies', 'protecciondatos', 'accesibilidad', 'tramites', 'tramites-slug', '404',
]

/** ¿La página (nombre de ruta) tiene el diseño de la plantilla, con su cabecera de página? */
export function paginaConDisenoPropio(nombreRuta: unknown): boolean {
  return RUTAS_DISENO_PROPIO.includes(String(nombreRuta || '').replace(/^lang-/, '').toLowerCase())
}

/** Plantilla de diseño propio de la web (2 a 5) o null con la de siempre: las páginas interiores eligen así su diseño. */
export function useDisenoPropio() {
  const store = useStore()
  return computed(() => /^notaria[2-5]$/.test(String(store.NOTARIA?.Web?.Plantilla || '').toLowerCase()))
}

/**
 * Alineación justificada del cuerpo de un texto, tal como se marcó en el panel (css.Align) para el diseño 1.
 * Solo se mantiene el justificado: centrado y derecha los decide la plantilla (ref: https://trello.com/c/KhP7vRHV).
 */
export function alineacionTexto(css?: { Align?: string } | null): Record<string, string> | undefined {
  return css?.Align === 'justify' ? { textAlign: 'justify', hyphens: 'auto' } : undefined
}

/** Columnas del panel ("Número de columnas", 1 a 4) para las rejillas de las páginas interiores. */
export function columnasPanel(valor: unknown, defecto = 3): number {
  const n = Number(valor)
  return n >= 1 && n <= 4 ? n : defecto
}

/** Minutos de lectura de un HTML (200 palabras por minuto). */
export function minutosLectura(html?: string | null): number {
  const palabras = textoPlano(html).split(' ').filter(Boolean).length
  return Math.max(1, Math.ceil(palabras / 200))
}

/** Texto del panel con saltos de línea (descripciones de notarios y equipo), como lo pinta la plantilla de siempre. */
export function conSaltos(texto?: string | null): string {
  return String(texto || '').replace(/(?:\r\n|\r|\n)/g, '<br>')
}
